import { BASE_API_URL } from '../apiConfig';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from '../components/ConfirmationModal';
import { useTranslation } from 'react-i18next';

const Profile = () => {
    const { t, i18n } = useTranslation();
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        preferred_address: '',
        lat: null,
        lng: null,
        language: i18n.language,
        is_external_available: false,
        external_email_enabled: true,
        external_push_enabled: true
    });
    const [language, setLanguage] = useState(i18n.language);
    const [geocoding, setGeocoding] = useState(false);
    const [modalConfig, setModalConfig] = useState({ isOpen: false, title: '', message: '' });
    const navigate = useNavigate();

    useEffect(() => {
        const fetchProfile = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }
            const res = await fetch(`${BASE_API_URL}/users/me`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setFormData({
                    first_name: data.first_name || '',
                    last_name: data.last_name || '',
                    email: data.email || '',
                    password: '',
                    preferred_address: data.preferred_address || '',
                    lat: data.lat || null,
                    lng: data.lng || null,
                    language: data.language || i18n.language,
                    is_external_available: data.is_external_available || false,
                    external_email_enabled: data.external_email_enabled !== false,
                    external_push_enabled: data.external_push_enabled !== false
                });
                if (data.language && data.language !== i18n.language) {
                    i18n.changeLanguage(data.language);
                    setLanguage(data.language);
                }
            } else {
                console.error("Failed to fetch profile. Status:", res.status);
                if (res.status === 401 || res.status === 403) {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                }
                navigate('/login');
            }
        };
        fetchProfile();
    }, [navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const res = await fetch(`${BASE_API_URL}/users/me`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify(formData)
        });

        if (res.ok) {
            const data = await res.json();
            // Update local storage with new info if needed
            const user = JSON.parse(localStorage.getItem('user'));
            user.name = data.first_name;
            localStorage.setItem('user', JSON.stringify(user));

            setModalConfig({ isOpen: true, title: t('success'), message: t('profile_updated') });
            setFormData(prev => ({ ...prev, password: '' }));
        } else {
            const errData = await res.json();
            setModalConfig({ isOpen: true, title: t('error'), message: errData.error || t('error') });
        }
    };

    const handleLanguageChange = (e) => {
        const newLang = e.target.value;
        setLanguage(newLang);
        setFormData(prev => ({ ...prev, language: newLang })); // Update form data
        i18n.changeLanguage(newLang);
        localStorage.setItem('language', newLang);
    };

    const handleAddressBlur = async () => {
        if (!formData.preferred_address || formData.preferred_address.trim() === '') {
            setFormData(prev => ({ ...prev, lat: null, lng: null }));
            return;
        }

        setGeocoding(true);
        try {
            const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(formData.preferred_address)}&limit=1`);
            const data = await response.json();
            if (data && data.length > 0) {
                setFormData(prev => ({
                    ...prev,
                    lat: parseFloat(data[0].lat),
                    lng: parseFloat(data[0].lon)
                }));
            }
        } catch (error) {
            console.error("Geocoding error:", error);
        } finally {
            setGeocoding(false);
        }
    };

    return (
        <div className="auth-page">
            <div className="auth-container" style={{ marginTop: '20px' }}>
                <div className="auth-header">
                    <h2>{t('your_profile')}</h2>
                    <p className="subtitle">{t('profile_subtitle')}</p>
                </div>
                <form onSubmit={handleSubmit} className="auth-form">
                    <div className="form-group">
                        <label>{t('first_name')}</label>
                        <input type="text" value={formData.first_name} onChange={e => setFormData({ ...formData, first_name: e.target.value })} required />
                    </div>
                    <div className="form-group">
                        <label>{t('last_name')} <i>({t('optional')})</i></label>
                        <input type="text" value={formData.last_name} onChange={e => setFormData({ ...formData, last_name: e.target.value })} />
                    </div>
                    <div className="form-group">
                        <label>{t('email')}</label>
                        <input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required />
                    </div>
                    <div className="form-group">
                        <label>{t('new_password')}</label>
                        <input type="password" placeholder={t('password_placeholder')} value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} />
                    </div>
                    
                    <div className="form-group">
                        <label>{t('language')}</label>
                        <select 
                            value={language} 
                            onChange={handleLanguageChange} 
                            className="form-input"
                            style={{ 
                                width: '100%', 
                                padding: '12px', 
                                borderRadius: '8px', 
                                border: '1px solid #ddd',
                                fontSize: '1rem'
                            }}
                        >
                            <option value="it">🇮🇹 {t('it')}</option>
                            <option value="en">🇬🇧 {t('en')}</option>
                            <option value="fr">🇫🇷 {t('fr')}</option>
                            <option value="es">🇪🇸 {t('es')}</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>{t('preferred_address')}</label>
                        <input
                            type="text"
                            placeholder={t('address_placeholder')}
                            value={formData.preferred_address}
                            onChange={e => setFormData({ ...formData, preferred_address: e.target.value })}
                            onBlur={handleAddressBlur}
                        />
                        {geocoding && <small style={{ color: '#666' }}>{t('geocoding_checking')}</small>}
                        {!geocoding && formData.lat && <small style={{ color: '#2e7d32' }}>{t('address_found')}</small>}
                        {!geocoding && !formData.lat && formData.preferred_address && <small style={{ color: '#c62828' }}>{t('address_not_found')}</small>}
                    </div>

                    <div className="form-group" style={{ 
                        background: formData.is_external_available ? '#fff' : '#f9f9f9', 
                        padding: '20px', 
                        borderRadius: '12px', 
                        border: '1px solid',
                        borderColor: formData.is_external_available ? 'var(--primary-color)' : '#eee',
                        transition: 'all 0.3s ease',
                        boxShadow: formData.is_external_available ? '0 4px 12px rgba(0,0,0,0.05)' : 'none'
                    }}>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '15px', margin: 0 }}>
                            <div style={{ position: 'relative', width: '44px', height: '24px', flexShrink: 0 }}>
                                <input
                                    type="checkbox"
                                    checked={formData.is_external_available}
                                    onChange={e => setFormData({ ...formData, is_external_available: e.target.checked })}
                                    style={{ opacity: 0, width: 0, height: 0 }}
                                />
                                <span style={{
                                    position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                                    backgroundColor: formData.is_external_available ? 'var(--primary-color)' : '#ccc',
                                    transition: '.4s', borderRadius: '34px'
                                }}>
                                    <span style={{
                                        position: 'absolute', content: '""', height: '18px', width: '18px', 
                                        left: formData.is_external_available ? '22px' : '4px', bottom: '3px',
                                        backgroundColor: 'white', transition: '.4s', borderRadius: '50%'
                                    }}></span>
                                </span>
                            </div>
                            <div style={{ flex: 1 }}>
                                <strong style={{ display: 'block', fontSize: '1.1rem', color: formData.is_external_available ? 'var(--primary-color)' : '#333' }}>
                                    {t('external_availability_title')}
                                </strong>
                                <small style={{ color: '#666', lineHeight: '1.4' }}>{t('external_availability_desc')}</small>
                            </div>
                        </label>

                        {formData.is_external_available && (
                            <div style={{ 
                                marginTop: '20px', 
                                paddingTop: '15px', 
                                borderTop: '1px dashed #eee',
                                display: 'flex', 
                                flexDirection: 'column', 
                                gap: '12px' 
                            }}>
                                <p style={{ margin: '0 0 5px 0', fontSize: '0.85rem', fontWeight: 'bold', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    {t('notification_channels')}
                                </p>
                                
                                <label style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    cursor: 'pointer', 
                                    gap: '12px', 
                                    padding: '10px 15px',
                                    background: '#f8f9fa',
                                    borderRadius: '8px',
                                    transition: 'background 0.2s'
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={formData.external_email_enabled}
                                        onChange={e => setFormData({ ...formData, external_email_enabled: e.target.checked })}
                                        style={{ width: '18px', height: '18px' }}
                                    />
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontSize: '1.2rem' }}>📧</span>
                                        <span style={{ fontSize: '0.95rem' }}>{t('receive_scouting_email')}</span>
                                    </div>
                                </label>

                                <label style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    cursor: 'pointer', 
                                    gap: '12px', 
                                    padding: '10px 15px',
                                    background: '#f8f9fa',
                                    borderRadius: '8px',
                                    transition: 'background 0.2s'
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={formData.external_push_enabled}
                                        onChange={e => setFormData({ ...formData, external_push_enabled: e.target.checked })}
                                        style={{ width: '18px', height: '18px' }}
                                    />
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontSize: '1.2rem' }}>📱</span>
                                        <span style={{ fontSize: '0.95rem' }}>{t('receive_scouting_push')}</span>
                                    </div>
                                </label>
                            </div>
                        )}
                    </div>

                    <div className="admin-actions" style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                        <button type="button" className="secondary-btn" onClick={() => navigate('/')}>{t('back')}</button>
                        <button type="submit" className="primary-btn">{t('save_changes')}</button>
                    </div>
                </form>
            </div>

            <ConfirmationModal
                isOpen={modalConfig.isOpen}
                onClose={() => setModalConfig({ isOpen: false, title: '', message: '' })}
                title={modalConfig.title}
                message={modalConfig.message}
                isAlert={true}
            />
        </div>
    );
};

export default Profile;
